import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import useAccessibleDialog from '../../hooks/useAccessibleDialog';
import useShippingRequest from '../../hooks/useShippingRequest';
import distributorService from '../../services/distributorService';
import { usd } from './cashMoney';
import { readCashDeposit, sameCashDeposit } from './cashDepositContract';
import {
  readCashWireClearance,
  validMerchantDay,
  validCashWireReference,
} from './cashReviewContract';

const unwrap = (response) => response?.data?.data;
export default function CashWireDepositReview({ distributorId, deposit, onClose, onUpdated }) {
  const t = useT();
  const [saved, setSaved] = useState(null),
    [error, setError] = useState('');
  const [reference, setReference] = useState(''),
    [day, setDay] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false),
    [notice, setNotice] = useState('');
  const pending = useRef(false),
    intent = useRef(null);
  const { begin, version } = useShippingRequest(`cash-wire:${distributorId}:${deposit._id}`);
  const { dialogRef, titleId } = useAccessibleDialog({
    open: true,
    onClose,
    closeDisabled: busy || uncertain,
  });
  const load = async (request) =>
    readCashWireClearance(
      unwrap(
        await distributorService.adminCashWireClearance(distributorId, deposit._id, {
          signal: request.signal,
        })
      ),
      deposit
    );
  useEffect(() => {
    const request = begin();
    setSaved(null);
    load(request)
      .then((row) => {
        if (request.isCurrent()) setSaved(row);
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Wire clearance could not load. Reload before confirming.');
      });
    // Keep the original uncertain confirmation for explicit recovery after a scope recheck.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [begin, version]);
  const recover = async () => {
    if (pending.current) return;
    const request = begin();
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      const row = await load(request);
      if (!request.isCurrent()) return;
      setSaved(row);
      const original = intent.current;
      if (
        original &&
        (!row.wireClearance ||
          ['requestKey', 'sourceReference', 'clearedAt'].some(
            (key) => row.wireClearance[key] !== original[key]
          ))
      )
        throw new Error('Unconfirmed wire');
      intent.current = null;
      setUncertain(false);
      setNotice(
        row.wireClearance
          ? 'Cleared wire recorded. Refresh the bank to verify the available balance.'
          : 'Wire clearance refreshed. No funds were added.'
      );
      if (row.wireClearance) onUpdated?.();
    } catch {
      if (request.isCurrent())
        setError(
          'Wire confirmation remains unconfirmed. Keep this request and contact MedEffects; do not record another wire.'
        );
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || uncertain || !saved?.canConfirm || !confirmed) return;
    if (!validCashWireReference(reference) || !validMerchantDay(day))
      return setError(
        'Enter the bank reference and actual clearance date, then confirm the cleared funds.'
      );
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey)
      return setError('A secure request key is unavailable. Reload before continuing.');
    const input = {
      requestKey,
      expectedRevision: saved.expectedRevision,
      amountCents: deposit.amountCents,
      currency: 'USD',
      sourceReference: reference.trim(),
      clearedAt: day,
      confirmedCleared: true,
    };
    const request = begin();
    intent.current = input;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = readCashDeposit(
        unwrap(
          await distributorService.confirmAdminCashWireClearance(distributorId, deposit._id, input)
        )
      );
      if (!request.isCurrent()) return;
      if (!sameCashDeposit(receipt, deposit) || receipt.status !== 'credited')
        throw new Error('Unconfirmed wire credit');
      const row = await load(request);
      if (!request.isCurrent()) return;
      if (
        !row.wireClearance ||
        ['requestKey', 'sourceReference', 'clearedAt'].some(
          (key) => row.wireClearance[key] !== input[key]
        )
      )
        throw new Error('Unconfirmed wire proof');
      setSaved(row);
      intent.current = null;
      setNotice('Cleared wire recorded. Refresh the bank to verify the available balance.');
      onUpdated?.();
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError(
          'Wire confirmation could not be verified. Recover the saved clearance before continuing.'
        );
      }
    } finally {
      pending.current = false;
      if (request.isCurrent()) setBusy(false);
    }
  };
  return (
    <div className="me-cash-review-overlay">
      <section
        className="me-cash me-cash-review-dialog"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="me-cash-heading">
          <h2 id={titleId}>
            {t('Cleared wire deposit')} · {usd(deposit.amountCents)}
          </h2>
          <button type="button" disabled={busy || uncertain} onClick={onClose}>
            {t('Close review')}
          </button>
        </div>
        <p>{t('Request reference: {reference}', { reference: deposit._id })}</p>
        <p>
          {t(
            'One cleared wire must match this exact USD amount. Verify funds in the bank; a customer receipt alone is not clearance evidence.'
          )}
        </p>
        {error && <p role="alert">{t(error)}</p>}
        {notice && <p role="status">{t(notice)}</p>}
        {busy && <p role="status">{t('Checking wire clearance…')}</p>}
        {saved?.wireClearance && (
          <p>
            {t('Bank reference: {reference} · Cleared: {date}', {
              reference: saved.wireClearance.sourceReference,
              date: saved.wireClearance.clearedAt.slice(0, 10),
            })}
          </p>
        )}
        {saved?.canConfirm && !uncertain && (
          <form onSubmit={submit}>
            <fieldset disabled={busy} className="me-cash-grid">
              <label>
                {t('Bank confirmation reference')}
                <input
                  value={reference}
                  maxLength={120}
                  onChange={(event) => {
                    setReference(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </label>
              <label>
                {t('Bank clearance date')}
                <input
                  type="date"
                  value={day}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(event) => {
                    setDay(event.target.value);
                    setConfirmed(false);
                  }}
                />
              </label>
              <label className="me-cash-authorize">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                {t('I verified one cleared wire for this exact amount and request.')}
              </label>
              <button className="me-cash-primary" type="submit" disabled={!confirmed}>
                {t('Confirm cleared wire')}
              </button>
            </fieldset>
          </form>
        )}
        <button type="button" disabled={busy} onClick={recover}>
          {t(uncertain ? 'Recover wire clearance' : 'Reload wire clearance')}
        </button>
      </section>
    </div>
  );
}
